import { StateCreator } from 'zustand';

export interface TutorialStep {
  id: string;
  nextId?: string;
  prevId?: string;
  targetId: string | undefined;
  highlightTargetIds?: string[];
}

export interface TutorialStoreValue {
  currentStep: TutorialStep | undefined;
  steps: TutorialStep[];
  isTransitioning: boolean;

  start: () => Promise<void>;
  goNext: () => Promise<void>;
  goBack: () => Promise<void>;
  close: () => Promise<void>;
}

export const createTutorialStoreSlice = <T extends object>(
  steps: TutorialStep[],
  options?: {
    onBeforeChange?: (step: TutorialStep | undefined) => Promise<void>;
    onAfterChange?: (step: TutorialStep | undefined) => Promise<void>;
    onClose?: () => Promise<void>;
  }
) => {
  const initializer: StateCreator<
    TutorialStoreValue & T,
    [],
    [],
    TutorialStoreValue
  > = (set, get) => {
    const changeStep = async (step: TutorialStep | undefined) => {
      set((state) => ({ ...state, isTransitioning: true }));

      if (options?.onBeforeChange) {
        await options.onBeforeChange(step);
      }
      set((state) => ({ ...state, currentStep: step, isTransitioning: false }));
      if (options?.onAfterChange) {
        await options.onAfterChange(step);
      }
    };
    return {
      isTransitioning: false,
      currentStep: undefined,
      steps,
      start: async () => {
        await changeStep(steps[0]);
      },
      goNext: async () => {
        const currentStep = get().currentStep;
        const nextStep = steps.find((step) => step.id === currentStep?.nextId);
        changeStep(nextStep);
      },
      goBack: async () => {
        const currentStep = get().currentStep;
        const prevStep = steps.find((step) => step.id === currentStep?.prevId);
        changeStep(prevStep);
      },
      close: async () => {
        changeStep(undefined);
        if (options?.onClose) {
          await options.onClose();
        }
        set((state) => ({ ...state, currentStep: undefined }));
      },
    };
  };
  return initializer;
};
